import * as React from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, CircularProgress, TableSortLabel } from '@mui/material';
import * as PXBColors from '@brightlayer-ui/colors';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { EmptyState } from '@brightlayer-ui/react-components';
import { Order } from '../../types/table';

export interface Column {
    id: string;
    label: React.ReactNode;
    width?: string;
    align?: 'right';
    format?: (value: any, record: Data) => React.ReactNode;
    sorting?: boolean;
    hideSortIcon?: boolean;
    sortColumn?: string;
    sortDirection?: string;
}

interface Data {
    [key: string]: any;
}

interface DataTableProps {
    isLoading?: boolean;
    columns: Column[];
    records: Data[];
    count: number;
    emptyListMessage: string;
    currentPage: number;
    onPageChange: (pageNumber: number) => void;
    onRowsPerPageChange: (newRowsPerPage: number) => void;
    onSort?: (column: string, direction: string) => void;
}

const DataTable: React.FC<DataTableProps> = ({ isLoading = false, emptyListMessage, columns, records, count, currentPage, onPageChange, onRowsPerPageChange, onSort = (columns) => { } }) => {
    const { t } = useLanguageLocale();

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [data, setData] = React.useState<Data[]>(records);
    const [orderBy, setOrderBy] = React.useState<string>('');
    const [order, setOrder] = React.useState<string>('asc');
    const [headerColumns, setHeaderColumns] = React.useState<Array<any>>(columns);

    const handleChangePage = async (event: unknown, newPage: number) => {
        setPage(newPage);
        onPageChange(newPage);
    };

    const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = +event.target.value;
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        onRowsPerPageChange(newRowsPerPage);
    };

    React.useEffect(() => {
        setHeaderColumns(columns);
    }, [columns])

    React.useEffect(() => {
        setData(records);
    }, [records])

    React.useEffect(() => {
        setPage(currentPage);
    }, [currentPage])

    return (
        <Paper>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            {headerColumns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    style={{ width: column.width }}>
                                    <TableSortLabel
                                        active={orderBy === column.sortColumn}
                                        direction={column.sortDirection}
                                        hideSortIcon={column.hideSortIcon}
                                        onClick={() => onSort(column.sortColumn, column.sortDirection)}>
                                        {column.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    {
                        !isLoading && records.length > 0 &&
                        <TableBody>
                            {data.map((row, index) => (
                                <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                                    {columns.map((column) => {
                                        const value = row[column.id];
                                        return (
                                            <TableCell key={column.id} align={column.align}>
                                                {column.format ? column.format(value, row) : value}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    }
                </Table>
            </TableContainer>
            {isLoading && <EmptyState style={{ flex: 1, minHeight: 400, backgroundColor: PXBColors.white[50], }}
                icon={<CircularProgress id="progress-spinner" />} title="" placeholder={undefined} />}
            {!isLoading && records.length === 0 && <EmptyState style={{ flex: 1, minHeight: 400, backgroundColor: PXBColors.white[50] }}
                title={emptyListMessage} icon={undefined} placeholder={undefined} />}
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={count}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('LABELS.ITEMS_PER_PAGE')}
                showFirstButton
                showLastButton
            />
        </Paper>
    );
};

export default DataTable;
